import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, brl, formatApiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Printer, XCircle } from "lucide-react";
import { FiscalPanel } from "@/components/fiscal/FiscalPanel";
import { PaymentsBlock } from "@/components/PaymentsBlock";
import { printAppPage } from "@/lib/print";
import { ReceiptButtons } from "@/components/ReceiptButtons";
import { useProductCosts, BudgetProfitTotals } from "@/components/BudgetProfit";
import { qtyFmt } from "@/components/erp/ErpUi";

export default function SaleDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const costMap = useProductCosts();
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const load = useCallback(() => api.get(`/sales/${id}`).then(({ data }) => setS(data)).catch((e) => toast.error(formatApiError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const cancel = async () => {
    if (!window.confirm("Cancelar esta venda? O estoque volta e a conta a receber é removida.")) return;
    setBusy(true);
    try { await api.post(`/sales/${id}/cancel`); toast.success("Venda cancelada"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto mt-10" />;
  const done = s.status === "concluida";
  return (
    <div className="space-y-5 animate-fade-up max-w-4xl">
      <Link to="/owner/balcao" className="text-zinc-400 hover:text-zinc-200 text-sm inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Venda balcão</Link>
      <div className="rounded-2xl border border-card2-border bg-card2 p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] font-mono-caps text-zinc-500">Venda balcão · {fmtDateTime(s.created_at)}</div>
            <h1 data-testid="sale-title" className="font-display text-2xl font-black uppercase text-zinc-100">V{s.number} · {s.customer_name || "Consumidor final"}</h1>
            {s.status === "cancelada" && <span data-testid="sale-canceled" className="text-xs font-bold uppercase text-red-300">Cancelada</span>}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="sale-print-receipt" onClick={() => printAppPage(`/owner/balcao/${id}/comprovante`)} className="bg-zinc-100 text-zinc-900 hover:bg-white font-bold uppercase"><Printer className="h-4 w-4 mr-1" /> Imprimir comprovante</Button>
            {done && <Button data-testid="sale-cancel-btn" variant="outline" disabled={busy} onClick={cancel} className="bg-zinc-900 border-red-900 text-red-300 hover:bg-red-950/50"><XCircle className="h-4 w-4 mr-1" /> Cancelar venda</Button>}
          </div>
        </div>
        <ul className="divide-y divide-zinc-800 text-sm">{s.items.map((it, i) => (
          <li key={i} className="py-2 flex justify-between gap-2"><span className="text-zinc-200">{qtyFmt(it.qty)}× {it.description}{it.product_id ? <span className="ml-1 text-[11px] text-zinc-500">peça</span> : <span className="ml-1 text-[11px] text-zinc-500">serviço</span>}</span><span className="text-zinc-100">{brl(it.total)}</span></li>
        ))}</ul>
        <div className="text-sm text-zinc-400 space-y-1">
          {s.discount_value > 0 && <div className="flex justify-between"><span>Desconto</span><span>− {brl(s.discount_value)}</span></div>}
          <div className="flex justify-between text-lg font-bold text-zinc-100"><span>Total</span><span data-testid="sale-detail-total" className="text-green-neon">{brl(s.total)}</span></div>
        </div>
        <BudgetProfitTotals testid="sale-detail-profit" items={(s.items || []).map((it) => ({ product_id: it.product_id, quantity: it.qty, unit_price: it.unit_price, discount: 0 }))} costMap={costMap} />
        <PaymentsBlock key={`${s.status}-${s.payment?.recorded_at || "none"}`} source="sales" id={id} canEdit={done} onChanged={load} />
        {done && s.payment && <ReceiptButtons source="sale" id={id} />}
      </div>
      <div className="rounded-2xl border border-card2-border bg-card2 p-5">
        <FiscalPanel order={s} source="sales" onOrderChanged={load} autoPrompt={params.get("nf") === "1"} onPrompted={() => setParams({}, { replace: true })} />
      </div>
    </div>
  );
}
